import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { ClusterSymbolDc } from "@evergis/api";

import { useSegmentStyle } from "../../../../../modules/Style/hooks/useSegmentStyle";
import { getSegmentIndex } from "../../../../../utils/style";
import { partialUpdateSymbol } from "../../../../../ducks/style/style";
import { useAction } from "../../../../../hooks/useAction";
import { getScaleSegments, getSelectedSegment, getStyleChilds } from "../../../../../ducks/style/selectors";
import { castScaleToResolution } from "../../../../../evergis/core/scale";
import { StyleSymbolType, SvgPointSymbol } from "../../../../../interfaces/style";
import { useDialogReset } from "../../../../../hooks/useDialogs";

export const useClusterSizeRanges = () => {
  const segmentStyle = useSegmentStyle();
  const resetDialog = useDialogReset();
  const updateSymbol = useAction(partialUpdateSymbol);
  const selectedSegment = useSelector(getSelectedSegment);
  const children = useSelector(getStyleChilds);
  const scaleSegments = useSelector(getScaleSegments);
  const [from, changeFrom] = useState((segmentStyle.symbol as ClusterSymbolDc).minClusterSize);
  const [to, changeTo] = useState((segmentStyle.symbol as ClusterSymbolDc).maxClusterSize);

  const recalc = useCallback(() => {
    const segmentIndex = getSegmentIndex({ scaleSegments, selectedSegment });
    const resolution = castScaleToResolution([scaleSegments[segmentIndex], scaleSegments[segmentIndex + 1]]);
    const childIndex = children?.findIndex(
      ({ minResolution, maxResolution }) => !maxResolution || minResolution === resolution[0],
    );

    updateSymbol({
      symbol: { ...segmentStyle.symbol, minClusterSize: from, maxClusterSize: to } as Partial<StyleSymbolType>,
      index: childIndex,
    });
    resetDialog();
  }, [children, from, resetDialog, scaleSegments, segmentStyle.symbol, selectedSegment, to, updateSymbol]);

  const getSingleObjectSize = useCallback(() => {
    changeFrom(((segmentStyle.symbol as ClusterSymbolDc).singleObjectSymbol as SvgPointSymbol).size as number);
  }, [segmentStyle.symbol]);

  return {
    from,
    to,
    changeFrom,
    changeTo,
    recalc,
    getSingleObjectSize,
  };
};
